// Общий перехват «сесія закінчилась» у запросов всех поверхностей.
//
// ⚠⚠ **Зачем в оболочке, а не в каждой поверхности** (`carry.md`, строка таска
// 27; `S-00/return-to.ts`). До оболочки ответ о законченной сессии видела
// только `S-00`: поверхность, на которой человека прервали, печатала отказ у
// себя, и возврат на прерванный адрес (решение оркестратора 71 (б)) не
// наступал никогда — запомнить адрес было некому. Здесь один перехват на все
// обращения к `/api/`: пришёл ответ 401 с кодом `session_expired` — оболочка
// запоминает, где человека прервали, и ведёт на вход.
//
// ⚠ Отличить «сессия кончилась посреди работы» от «сессии не было вовсе»
// (вход по закладке) сервер не может: `session_of` отвечает одним и тем же.
// Поэтому оболочка помнит, видел ли **этот** браузер живую сессию (`markLive`),
// — тем же хранилищем и по той же причине, что и прерванный адрес
// (`sessionStorage`, `return-to.ts`). Живой не было — это старт, а не
// прерванная работа: вход, и после него дашборд (`G93`).

const LIVE = "fortify.live";

function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Этот браузер видит живую сессию: вход состоялся или `/me` ответил. */
export function markLive(): void {
  store()?.setItem(LIVE, "1");
}

/** Сессии больше нет: выход или перехваченное окончание. */
export function forgetLive(): void {
  store()?.removeItem(LIVE);
}

/** Видел ли этот браузер живую сессию. */
export function wasLive(): boolean {
  return store()?.getItem(LIVE) === "1";
}

/** Код случая «сесія закінчилась» (`access.service.SESSION_EXPIRED`). */
export const SESSION_EXPIRED = "session_expired";

/**
 * Ответы, которые перехват не трогает.
 *
 * ⚠ Ручки входа отвечают 401 и сами, и по другой причине: неверный пароль при
 * входе и при смене пароля — это отказ формы, а не конец сессии, и его
 * показывает `S-00`. Перехват по коду случая их и так не тронул бы, но адрес
 * назван, чтобы правило не держалось на одном коде.
 */
const OWN_ANSWERS = ["/api/auth/login", "/api/auth/password", "/api/screen-words"];

/** Адрес запроса строкой — у `fetch` он приходит тремя видами. */
function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.pathname;
  return input.url;
}

/** Путь без хоста: `https://x/api/s-01/free?y` → `/api/s-01/free`. */
function pathOf(url: string): string {
  try {
    return new URL(url, "https://local").pathname;
  } catch {
    return url;
  }
}

/** Ответ говорит «сесія закінчилась» кодом случая. Тело не трогается. */
export async function saysSessionOver(answer: Response): Promise<boolean> {
  if (answer.status !== 401) return false;
  try {
    const body: unknown = await answer.clone().json();
    return (
      body !== null && typeof body === "object" &&
      (body as { kind?: unknown }).kind === SESSION_EXPIRED
    );
  } catch {
    return false;
  }
}

/**
 * Поставить перехват. Возвращает снятие.
 *
 * `onOver` зовётся не больше одного раза на волну ответов: пять поверхностных
 * запросов, упавших одновременно, — это одно окончание сессии, а не пять.
 * Волна кончается первым удачным ответом `/api/` — после нового входа.
 */
export function watchSessions(onOver: () => void): () => void {
  const before = window.fetch;
  let fired = false;
  const watched: typeof window.fetch = async (input, init) => {
    const answer = await before(input, init);
    const path = pathOf(urlOf(input));
    if (!path.startsWith("/api/") || OWN_ANSWERS.includes(path)) return answer;
    if (answer.ok) {
      // Сессия снова живая (вошли заново) — следующее окончание снова событие.
      fired = false;
      return answer;
    }
    if (!fired && (await saysSessionOver(answer))) {
      fired = true;
      onOver();
    }
    return answer;
  };
  window.fetch = watched;
  return () => {
    // Сняли только свой: если поверх поставили ещё один, он остаётся.
    if (window.fetch === watched) window.fetch = before;
  };
}
